import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  startWith,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { PppShiftDetailDialogComponent } from '../../components/ppp-shift-detail-dialog/ppp-shift-detail-dialog.component';
import { PreferredPartnerService } from '../../preferred-partner.service';
import { ShiftEvaluation, ShiftEvaluationsResponse, SummaryRegion } from '../../types';
import { formatLocalDate, mondayDateFilter, parseISODateLocal } from '../../utils/date';

interface DriverGroup {
  driverId: number;
  driverName: string;
  shifts: ShiftEvaluation[];
  totalShifts: number;
  driverEligibleAtCloseCount: number;
}

interface DriversFilterValue {
  weekStart: Date | null;
  status: 'all' | 'eligible' | 'notEligible';
  regionId: number | null;
}

const PAGE_LIMIT = 200;

/**
 * /preferred-partner/drivers
 *
 * Surfaces shift_evaluations grouped by driver. Filters:
 *   - weekStart (defaults to /calendar/current_week.weekStart),
 *   - status (eligible / not),
 *   - region.
 *
 * Clicking a row opens PppShiftDetailDialog with the four rules and
 * defensively-rendered violations.
 */
@Component({
  selector: 'lohi-ppp-driver-eligibility',
  templateUrl: './ppp-driver-eligibility.component.html',
  styleUrls: ['./ppp-driver-eligibility.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PppDriverEligibilityComponent implements OnInit, OnDestroy {
  public readonly form: FormGroup;
  public readonly groups$: Observable<DriverGroup[]>;
  public readonly regions$: Observable<SummaryRegion[]>;
  public readonly loading$$ = new BehaviorSubject<boolean>(false);
  public readonly error$$ = new BehaviorSubject<string | null>(null);
  public readonly mondayDateFilter = mondayDateFilter;

  private destroy$$ = new Subject<void>();

  constructor(private fb: FormBuilder, private pppService: PreferredPartnerService, private dialog: MatDialog) {
    this.form = this.fb.group({
      weekStart: [null as Date | null],
      status: ['all'],
      regionId: [null as number | null],
    });

    this.regions$ = this.pppService.summary$.pipe(map((s) => s?.byRegion || []));

    // Read form.value lazily on each emission (instead of capturing it via
    // startWith at constructor time): when the user lands here from another
    // page that already filled summary$ / currentWeek$, ngOnInit's
    // combineLatest emits synchronously and patches the form BEFORE the
    // async pipe subscribes. Using `map(() => this.form.value)` ensures the
    // first emission picks up the patched values instead of the stale
    // constructor snapshot.
    const filters$ = this.form.valueChanges.pipe(
      startWith(null),
      map(() => this.form.value as DriversFilterValue),
      distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
    );

    this.groups$ = filters$.pipe(
      filter((f: DriversFilterValue) => !!f.weekStart),
      tap(() => {
        this.loading$$.next(true);
        this.error$$.next(null);
      }),
      switchMap((f: DriversFilterValue) =>
        this.pppService
          .listShiftEvaluations$({
            weekStart: formatLocalDate(f.weekStart) || '',
            driverEligibleAtClose: f.status === 'all' ? undefined : f.status === 'eligible',
            regionId: f.regionId || undefined,
            limit: PAGE_LIMIT,
            offset: 0,
          })
          .pipe(
            tap(() => this.loading$$.next(false)),
            map((resp: ShiftEvaluationsResponse) => this.groupByDriver(resp.shiftEvaluations)),
            catchError((err) => {
              this.loading$$.next(false);
              this.error$$.next(err?.message || 'Failed to load shift evaluations');
              return of<DriverGroup[]>([]);
            }),
          ),
      ),
    );
  }

  public ngOnInit(): void {
    // Seed weekStart from /calendar/current_week and regionId from the
    // first region in the summary in a single patch, so the user lands on
    // a populated view (and we avoid a double fetch from two patches).
    combineLatest([
      this.pppService.currentWeek$.pipe(filter((cw) => !!cw)),
      this.pppService.summary$.pipe(filter((s) => !!s)),
    ])
      .pipe(take(1), takeUntil(this.destroy$$))
      .subscribe(([cw, summary]) => {
        const regions = summary?.byRegion || [];
        const updates: Partial<DriversFilterValue> = {};
        if (cw && !this.form.value.weekStart) {
          updates.weekStart = parseISODateLocal(cw.weekStart);
        }
        if (regions.length > 0 && this.form.value.regionId == null) {
          updates.regionId = regions[0].regionId;
        }
        if (Object.keys(updates).length > 0) {
          this.form.patchValue(updates, { emitEvent: true });
        }
      });
  }

  public ngOnDestroy(): void {
    this.destroy$$.next();
    this.destroy$$.complete();
  }

  public openShift(shift: ShiftEvaluation): void {
    this.dialog.open(PppShiftDetailDialogComponent, {
      data: { shift },
      width: '720px',
      autoFocus: false,
    });
  }

  public trackByGroup(index: number, item: DriverGroup): number {
    return item.driverId;
  }

  private groupByDriver(shifts: ShiftEvaluation[]): DriverGroup[] {
    const byDriver = new Map<number, DriverGroup>();
    for (const shift of shifts) {
      let group = byDriver.get(shift.driverId);
      if (!group) {
        group = {
          driverId: shift.driverId,
          driverName: shift.driverName,
          shifts: [],
          totalShifts: 0,
          driverEligibleAtCloseCount: 0,
        };
        byDriver.set(shift.driverId, group);
      }
      group.shifts.push(shift);
      group.totalShifts += 1;
      if (shift.driverEligibleAtClose) {
        group.driverEligibleAtCloseCount += 1;
      }
    }
    return Array.from(byDriver.values()).sort((a, b) => a.driverName.localeCompare(b.driverName));
  }
}
